import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

import { TOURS } from "./tours"
import { createTourDriver } from "./tourRunner"
import { loadCompleted, loadWelcomeSeen, saveCompleted, saveWelcomeSeen } from "./tourProgress"
import WelcomeDialog from "./WelcomeDialog.jsx"

// Menü-/Dialog-Transition erst ausklingen lassen, sonst misst driver.js verschobene Elemente
const START_DELAY_MS = 250

const TourContext = createContext(null)

export function TourProvider({ children }) {
  const [completed, setCompleted] = useState(loadCompleted)
  const [welcomeOpen, setWelcomeOpen] = useState(() => !loadWelcomeSeen())
  const driverRef = useRef(null)
  const startTimerRef = useRef(null)

  const startTour = useCallback((id) => {
    const tour = TOURS.find((t) => t.id === id)
    if (!tour) return
    clearTimeout(startTimerRef.current)
    driverRef.current?.destroy()
    const instance = createTourDriver(tour, {
      onComplete: () =>
        setCompleted((prev) => {
          if (prev.includes(id)) return prev
          const next = [...prev, id]
          saveCompleted(next)
          return next
        }),
      onEnd: () => {
        if (driverRef.current === instance) driverRef.current = null
      },
    })
    driverRef.current = instance
    startTimerRef.current = setTimeout(() => instance.drive(), START_DELAY_MS)
  }, [])

  const closeWelcome = useCallback(() => {
    saveWelcomeSeen()
    setWelcomeOpen(false)
  }, [])

  useEffect(
    () => () => {
      clearTimeout(startTimerRef.current)
      driverRef.current?.destroy()
    },
    [],
  )

  const value = useMemo(
    () => ({ tours: TOURS, completed, startTour }),
    [completed, startTour],
  )

  return (
    <TourContext.Provider value={value}>
      {children}
      <WelcomeDialog
        open={welcomeOpen}
        tours={TOURS}
        onStart={(id) => {
          closeWelcome()
          startTour(id)
        }}
        onClose={closeWelcome}
      />
    </TourContext.Provider>
  )
}

export function useTours() {
  const ctx = useContext(TourContext)
  if (!ctx) throw new Error("useTours must be used inside <TourProvider>")
  return ctx
}
